import { EmbeddedMessage } from '../../../..';
import { HostMessageType } from '../../../interfaces/host-message-types';

interface WebView2Bridge {
  postMessage: (message: EmbeddedMessage) => void;
}

interface HostApplicationBridge {
  /**
   * Host -> Web channel. Not used when Web sends to Host.
   * Kept in typing for completeness of the host contract.
   */
  postMessage?: (message: EmbeddedMessage) => void;
  /**
   * Web -> Host channel in CEF/custom hosts.
   * The host registers/implements this function and the web app calls it.
   */
  onMessage?: (message: EmbeddedMessage) => void;
  /**
   * Some hosts may expose the callback as PascalCase.
   * Prefer `onMessage`, but support this for compatibility.
   */
  OnMessage?: (message: EmbeddedMessage) => void;
}

interface WebkitBridge {
  messageHandlers?: {
    host?: {
      postMessage: (message: EmbeddedMessage) => void;
    };
    HostApplication?: {
      postMessage: (message: EmbeddedMessage) => void;
    };
  };
}

interface HostWindow {
  chrome?: {
    webview?: WebView2Bridge;
  } & Record<string, unknown>;
  webkit?: WebkitBridge;
  HostApplication?: HostApplicationBridge;
  parent?: HostWindow;
  postMessage?: (message: unknown, options: { targetOrigin: string }) => void;
}

/**
 * Ensures `window.HostApplication` exists.
 *
 * This keeps the CEF/custom contract discoverable from the web side,
 * even before native host wiring is attached.
 */
function ensureHostApplicationBridge(win: HostWindow): HostApplicationBridge {
  win.HostApplication ??= {};

  return win.HostApplication;
}

/**
 * Sends a typed message from Web App to Host App.
 *
 * Transports (attempted in this order; all available transports may be used):
 * 1) WebView2: `window.chrome.webview.postMessage`
 * 2) WKWebView: `window.webkit.messageHandlers.host.postMessage` (or HostApplication handler)
 * 3) CEF/custom: `window.HostApplication.onMessage`
 * 4) iframe fallback: `window.parent.postMessage`
 *
 * A host may therefore receive the same logical message on multiple channels and
 * should handle messages in an idempotent manner.
 *
 * In plain browser development mode with no host bridge, this function is a no-op.
 *
 * @param type Message type identifier.
 * @param payload Optional data to include.
 * @param targetOrigin Target origin for postMessage (defaults to `'*'`).
 */
export function sendMessageToHost(
  type: HostMessageType,
  payload?: unknown,
  targetOrigin = '*'
): void {
  const baseMessage: EmbeddedMessage = { type, ...(payload !== undefined && { payload }) };
  const win = globalThis as unknown as HostWindow;
  const hostApplication = ensureHostApplicationBridge(win);

  // Web -> Host (WebView2): native host listens via chrome.webview message channel.
  if (win.chrome?.webview?.postMessage) {
    win.chrome.webview.postMessage({ ...baseMessage, channel: 'webview2' });
  }

  // Web -> Host (WKWebView default bridge): native side handles `messageHandlers.host`.
  if (win.webkit?.messageHandlers?.host?.postMessage) {
    win.webkit.messageHandlers.host.postMessage({ ...baseMessage, channel: 'wkwebview-host' });
  }

  // Web -> Host (WKWebView alternative naming): support HostApplication message handler key.
  if (win.webkit?.messageHandlers?.HostApplication?.postMessage) {
    win.webkit.messageHandlers.HostApplication.postMessage({
      ...baseMessage,
      channel: 'wkwebview-hostapplication',
    });
  }

  // Web -> Host (CEF/custom): web invokes host-registered callback directly.
  // This is an additional transport; hosts should use the `channel` field to de-duplicate
  // or route messages when multiple bridges are available.
  const hostOnMessage = hostApplication.onMessage ?? hostApplication.OnMessage;
  if (hostOnMessage) {
    hostOnMessage({ ...baseMessage, channel: 'hostapplication' });
  }

  // Web -> Host (iframe fallback): works for browser embedding without native bridge.
  // In top-level browser mode (`parent === window`) this is skipped, keeping send as a no-op.
  if (win.parent && win.parent !== win && win.parent.postMessage) {
    win.parent.postMessage({ ...baseMessage, channel: 'iframe' }, { targetOrigin });
  }
}
